import { getApiProjectsProjectIdNotifications } from '@shape-construction/api/src/api';
import {
  getApiNotificationsOverviewQueryOptions,
  getApiProjectsProjectIdNotificationsQueryOptions,
  usePostApiProjectsProjectIdNotificationsMarkAllRead,
  usePostApiProjectsProjectIdNotificationsNotificationIdMarkRead,
} from '@shape-construction/api/src/hooks';
import type { NotificationsOverviewSchema } from '@shape-construction/api/src/types';
import { infiniteQueryOptions, queryOptions, useQueryClient } from '@tanstack/react-query';
import { environment } from 'app/config/environment';

// Queries
export const getNotificationsOverviewQueryOptions = () => {
  return queryOptions({
    ...getApiNotificationsOverviewQueryOptions(),
    refetchInterval: Number(environment.NOTIFICATIONS_CHECK_INTERVAL_MS),
    refetchOnWindowFocus: false,
  });
};

// Per-project queries
export const getProjectNotificationsInfiniteQueryOptions = (projectId: string) => {
  return infiniteQueryOptions({
    queryKey: getApiProjectsProjectIdNotificationsQueryOptions(projectId).queryKey,
    queryFn: ({ pageParam, signal }) => getApiProjectsProjectIdNotifications(projectId, { ...pageParam }, { signal }),
    getNextPageParam: ({ meta }) => (meta.currentPage !== meta.totalPages ? { page: meta.currentPage + 1 } : undefined),
    refetchInterval: Number(environment.NOTIFICATIONS_CHECK_INTERVAL_MS),
    refetchOnWindowFocus: false,
    initialPageParam: { page: undefined as undefined | number },
    enabled: !!projectId,
  });
};

// Per-project mutations
export const useProjectNotificationMarkReadMutation = () => {
  const queryClient = useQueryClient();

  return usePostApiProjectsProjectIdNotificationsNotificationIdMarkRead({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(getApiProjectsProjectIdNotificationsQueryOptions(variables.projectId));
        queryClient.invalidateQueries(getApiNotificationsOverviewQueryOptions());
      },
    },
  });
};

export const useProjectNotificationMarkAllReadMutation = () => {
  const queryClient = useQueryClient();

  return usePostApiProjectsProjectIdNotificationsMarkAllRead({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(getApiProjectsProjectIdNotificationsQueryOptions(variables.projectId));
        queryClient.invalidateQueries(getApiNotificationsOverviewQueryOptions());
      },
    },
  });
};

// Helpers
export const getProjectUnreadCount = (
  overview: NotificationsOverviewSchema | undefined,
  projectId: string | undefined
) => {
  if (!overview || !projectId) return 0;
  return overview.unreadCountByProject.find((projectCount) => projectCount.projectId === projectId)?.totalUnread ?? 0;
};

export const getProjectTotalCount = (
  overview: NotificationsOverviewSchema | undefined,
  projectId: string | undefined
) => {
  if (!overview || !projectId) return 0;
  return overview.unreadCountByProject.find((projectCount) => projectCount.projectId === projectId)?.total ?? 0;
};

export const getOtherProjectsUnreadCount = (
  overview: NotificationsOverviewSchema | undefined,
  currentProjectId: string | undefined
) => {
  if (!overview) return 0;
  return overview.unreadCountByProject
    .filter((projectCount) => projectCount.projectId !== currentProjectId)
    .reduce((sum, projectCount) => sum + projectCount.totalUnread, 0);
};

export const getOtherProjectsWithUnreadCount = (
  overview: NotificationsOverviewSchema | undefined,
  currentProjectId: string | undefined
) => {
  if (!overview) return 0;
  return overview.unreadCountByProject.filter(
    (projectCount) => projectCount.projectId !== currentProjectId && projectCount.totalUnread > 0
  ).length;
};

export const getTotalUnreadCount = (overview: NotificationsOverviewSchema | undefined) => {
  if (!overview) return 0;
  return overview.unreadCountByProject.reduce((sum, projectCount) => sum + projectCount.totalUnread, 0);
};
